import axios from 'axios';
import { displayApiErrorFromAxios } from '../utils/apiErrorHandler';

// Create axios instance with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT || '/api',
});

// Singleton token getter — wired from App.tsx via setApiTokenGetter()
let getClerkToken: (() => Promise<string | null>) | null = null;

export const setApiTokenGetter = (getter: () => Promise<string | null>) => {
  getClerkToken = getter;
};

// Singleton ownerId — wired from OwnerContext via setApiOwnerId()
let currentOwnerId: string | null = null;

export const setApiOwnerId = (ownerId: string | null) => {
  currentOwnerId = ownerId;
};

export const getApiOwnerId = (): string | null => currentOwnerId;

// Add Clerk auth token and ownerId to every request
api.interceptors.request.use(async (config) => {
  if (getClerkToken) {
    const token = await getClerkToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  if (currentOwnerId) {
    config.headers['x-clerk-subdomain'] = currentOwnerId;
  }
  return config;
});

// Handle response errors — surface the shared ErrorModal/ErrorToast (with a
// visible Error ID) for API errors that carry a response. Network/cancel errors
// (no response) are re-rejected so callers / React Query can handle them.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const hasResponse =
      error && typeof error === 'object' && 'response' in error && (error as { response?: unknown }).response;
    if (hasResponse) {
      // displayApiErrorFromAxios throws ErrorDisplayed after showing the UI
      displayApiErrorFromAxios(error);
    }
    return Promise.reject(error);
  }
);

// API service for buildings
export const buildingsApi = {
  getAll: async () => {
    const response = await api.get('/buildings');
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/buildings/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/buildings', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/buildings/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/buildings/${id}`);
    return response.data;
  },
};

// API service for contacts
export const contactsApi = {
  getAll: async (params?: { search?: string; buildingId?: string; type?: string }) => {
    const response = await api.get('/contacts', { params });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/contacts', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/contacts/${id}`, data);
    return response.data;
  },
  archive: async (id: string) => {
    const response = await api.put(`/contacts/${id}`, { archivedAt: new Date().toISOString() });
    return response.data;
  },
  unarchive: async (id: string) => {
    const response = await api.put(`/contacts/${id}`, { archivedAt: null });
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  },
  convertToResident: async (id: string) => {
    const response = await api.post(`/contacts/${id}`, { action: 'convert-to-resident' });
    return response.data as { message: string; contactId: string; invoicesUpdated: number; paymentsUpdated: number };
  },
};
// API service for financial records (unified invoices, bills, service charge payments)
export const financialRecordsApi = {
  getAll: async (buildingId: string, params?: { type?: string; status?: string; contactId?: string; unitId?: string }) => {
    const response = await api.get('/financial-records', { params: { buildingId, ...params } });
    return response.data;
  },
  getByContact: async (contactId: string) => {
    const response = await api.get('/financial-records', { params: { buildingId: '__ALL__', contactId } });
    return response.data;
  },
  get: async (id: string, buildingId?: string) => {
    const response = await api.get(`/financial-records/${id}`, { params: { buildingId } });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/financial-records', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/financial-records/${id}`, data);
    return response.data;
  },
  reconcile: async (
    id: string,
    buildingId: string,
    bankTransactionId: string,
    bankTransactionBuildingId?: string,
    settlementAmount?: number,
  ) => {
    const response = await api.put(`/financial-records/${id}`, {
      action: 'reconcile',
      buildingId,
      bankTransactionId,
      bankTransactionBuildingId,
      settlementAmount,
    });
    return response.data;
  },
  unreconcile: async (
    id: string,
    buildingId: string,
    bankTransactionId: string,
    bankTransactionBuildingId?: string,
  ) => {
    const response = await api.put(`/financial-records/${id}`, {
      action: 'unreconcile',
      buildingId,
      bankTransactionId,
      bankTransactionBuildingId,
    });
    return response.data;
  },
  delete: async (id: string, buildingId: string) => {
    const response = await api.delete(`/financial-records/${id}`, { params: { buildingId } });
    return response.data;
  },
  // Task 2.5: New methods for form integration
  getDocumentUploadUrl: async (recordId: string, documentType: string, fileName: string) => {
    const response = await api.post(`/financial-records/${recordId}/document-upload-url`, { documentType, fileName });
    return response.data; // { url: string, key: string }
  },
  recordPayment: async (id: string, amount: number) => {
    const response = await api.post(`/financial-records/${id}/payments`, { amount });
    return response.data;
  },
  approve: async (id: string) => {
    const response = await api.put(`/financial-records/${id}/approve`, {});
    return response.data;
  },
  reject: async (id: string, reason: string) => {
    const response = await api.put(`/financial-records/${id}/reject`, { rejectionReason: reason });
    return response.data;
  },
};

// API service for ledger entries
export const journalApi = {
  getEntries: async (buildingId: string, filters: any = {}) => {
    const response = await api.get('/journal', { 
      params: { buildingId, ...filters } 
    });
    return response.data;
  },
  getEntry: async (id: string, buildingId?: string) => {
    const response = await api.get(`/journal/${id}`, {
      params: buildingId ? { buildingId } : {},
    });
    return response.data;
  },
  createEntry: async (data: any) => {
    const response = await api.post('/journal', data);
    return response.data;
  },
  updateEntry: async (id: string, data: any) => {
    const response = await api.put(`/journal/${id}`, data);
    return response.data;
  },
  deleteEntry: async (id: string) => {
    const response = await api.delete(`/journal/${id}`);
    return response.data;
  },
  postEntry: async (id: string, buildingId: string) => {
    const response = await api.post(`/journal/${id}/post`, { buildingId });
    return response.data;
  },
  reverseEntry: async (id: string, buildingId: string) => {
    const response = await api.post(`/journal/${id}/reverse`, { buildingId });
    return response.data;
  },
};

// API service for budgets
export const budgetsApi = {
  getAll: async (buildingId: string) => {
    const response = await api.get('/budgets', { params: { buildingId } });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/budgets/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/budgets', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/budgets/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/budgets/${id}`);
    return response.data;
  },
};

// API service for statements
export const statementsApi = {
  getAll: async (buildingId: string) => {
    const response = await api.get('/statements', { params: { buildingId } });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/statements/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/statements', data);
    return response.data;
  },
  generatePdf: async (statementId: string) => {
    const response = await api.post(`/statements/generate-pdf`, { statementId });
    return response.data;
  },
  /**
   * Generate service-charge statements for a building/period by aggregating the
   * building's FinancialRecords (service-charge-reconciliation feature).
   *
   * @param data.buildingId  the building to generate for
   * @param data.period      YYYY, YYYY-Qn, or YYYY-MM
   * @param data.categories  optional AccountingCategory codes/names to include
   * @param data.view        'summary' (per-category) or 'detailed' (per-record)
   */
  generate: async (data: {
    buildingId: string;
    period: string;
    categories?: string[];
    view?: 'summary' | 'detailed';
  }) => {
    const response = await api.post('/statements/generate', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/statements/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/statements/${id}`);
    return response.data;
  },
  send: async (id: string) => {
    const response = await api.post(`/statements/${id}/send`);
    return response.data;
  },
};

// API service for users
export const usersApi = {
  getAll: async (buildingId: string) => {
    const response = await api.get('/users', { params: { buildingId } });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/users', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

// API service for units/flats
export const unitsApi = {
  getAll: async (buildingId: string) => {
    const response = await api.get('/units', { params: { buildingId } });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/units/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/units', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/units/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/units/${id}`);
    return response.data;
  },
};

// API service for meetings
export const meetingsApi = {
  getAll: async (buildingId?: string) => {
    const params = buildingId ? { buildingId } : undefined;
    const response = await api.get('/meetings', { params });
    return response.data;
  },
  get: async (id: string, buildingId: string) => {
    const response = await api.get(`/meetings/${id}`, { params: { buildingId } });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/meetings', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/meetings/${id}`, data, {
      params: { buildingId: data.buildingId },
    });
    return response.data;
  },
  delete: async (id: string, buildingId: string) => {
    const response = await api.delete(`/meetings/${id}`, { params: { buildingId } });
    return response.data;
  },
  getUploadUrl: async (meetingId: string, fileName: string, contentType: string) => {
    const response = await api.get(`/meetings/${meetingId}`, {
      params: { action: 'upload-url', fileName, contentType },
    });
    return response.data as { url: string; key: string };
  },
  getDownloadUrl: async (meetingId: string, key: string) => {
    const response = await api.get(`/meetings/${meetingId}`, {
      params: { action: 'download-url', key },
    });
    return response.data as { url: string };
  },
};

// API service for reports
export const reportsApi = {
  getProfitLoss: async (buildingId: string, period: string) => {
    const response = await api.get(`/reports/profit-loss`, { 
      params: { buildingId, period } 
    });
    return response.data;
  },
  getBalanceSheet: async (buildingId: string, date: string) => {
    const response = await api.get(`/reports/balance-sheet`, { 
      params: { buildingId, date } 
    });
    return response.data;
  },
  getCashFlow: async (buildingId: string, period: string) => {
    const response = await api.get(`/reports/cashflow`, { 
      params: { buildingId, period } 
    });
    return response.data;
  },
  getServiceChargeAllocation: async (buildingId: string, year: string) => {
    const response = await api.get(`/reports/service-charge`, { 
      params: { buildingId, year } 
    });
    return response.data;
  },
  getAgedDebtors: async (buildingId: string, period: string) => {
    const response = await api.get(`/reports/aged-debtors`, { 
      params: { buildingId, period } 
    });
    return response.data;
  },
  getReserveFund: async (buildingId: string, period: string) => {
    const response = await api.get(`/reports/reserve-fund`, { 
      params: { buildingId, period } 
    });
    return response.data;
  },
  getBudgetVsActual: async (buildingId: string, year: string) => {
    const response = await api.get(`/reports/budget-vs-actual`, { 
      params: { buildingId, year } 
    });
    return response.data;
  },
  getAnnualAccounts: async (buildingId: string, year: string) => {
    const response = await api.get(`/reports/annual-accounts`, { 
      params: { buildingId, year } 
    });
    return response.data;
  },
  // ---------- New reports (FreeAgent / ICAEW parity) ----------
  getTrialBalance: async (buildingId: string, params: { date?: string; to?: string } = {}) => {
    const response = await api.get('/reports/trial-balance', { params: { buildingId, ...params } });
    return response.data;
  },
  getShowTransactions: async (
    buildingId: string,
    params: { nominalCode?: string; category?: string; from?: string; to?: string; period?: string }
  ) => {
    const response = await api.get('/reports/show-transactions', { params: { buildingId, ...params } });
    return response.data;
  },
  getBankReconciliation: async (
    buildingId: string,
    params: { bankAccountId: string; date?: string; to?: string }
  ) => {
    const response = await api.get('/reports/bank-reconciliation', { params: { buildingId, ...params } });
    return response.data;
  },
  getAgedCreditors: async (buildingId: string, params: { date?: string; to?: string } = {}) => {
    const response = await api.get('/reports/aged-creditors', { params: { buildingId, ...params } });
    return response.data;
  },
  getUnitStatement: async (
    buildingId: string,
    params: { unitId: string; from?: string; to?: string; period?: string }
  ) => {
    const response = await api.get('/reports/unit-statement', { params: { buildingId, ...params } });
    return response.data;
  },
  getSpendingCategories: async (
    buildingId: string,
    params: { from?: string; to?: string; period?: string; comparePrior?: boolean } = {}
  ) => {
    const response = await api.get('/reports/spending-categories', { params: { buildingId, ...params } });
    return response.data;
  },
  getCapitalAssets: async (buildingId: string, params: { date?: string } = {}) => {
    const response = await api.get('/reports/capital-assets', { params: { buildingId, ...params } });
    return response.data;
  },
  getAuditTrail: async (
    buildingId: string,
    params: { from?: string; to?: string; period?: string } = {}
  ) => {
    const response = await api.get('/reports/audit-trail', { params: { buildingId, ...params } });
    return response.data;
  },
  getManagementPack: async (
    buildingId: string,
    params: { reports?: string; from?: string; to?: string; period?: string } = {}
  ) => {
    const response = await api.get('/reports/management-pack', { params: { buildingId, ...params } });
    return response.data;
  },
  // ---------- Spec aliases ----------
  getIncomeExpenditure: async (buildingId: string, period: string) => {
    const response = await api.get('/reports/income-expenditure', { params: { buildingId, period } });
    return response.data;
  },
  getYearEnd: async (buildingId: string, year: string) => {
    const response = await api.get('/reports/year-end', { params: { buildingId, year } });
    return response.data;
  },
  getReserves: async (buildingId: string, period: string) => {
    const response = await api.get('/reports/reserves', { params: { buildingId, period } });
    return response.data;
  },
  getApportionment: async (buildingId: string, year: string) => {
    const response = await api.get('/reports/apportionment', { params: { buildingId, year } });
    return response.data;
  },
};

// API service for bank accounts
export const bankAccountsApi = {
  getAll: async (buildingId: string) => {
    const response = await api.get('/bank-accounts', { params: { buildingId } });
    return response.data;
  },
  get: async (id: string, buildingId: string) => {
    const response = await api.get(`/bank-accounts/${id}`, { params: { buildingId } });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/bank-accounts', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/bank-accounts/${id}`, data);
    return response.data;
  },
  delete: async (id: string, buildingId: string) => {
    const response = await api.delete(`/bank-accounts/${id}`, { params: { buildingId } });
    return response.data;
  },
};

// API service for bank transactions
export const bankTransactionsApi = {
  getAll: async (buildingId: string, bankAccountId?: string) => {
    const response = await api.get('/bank-transactions', { params: { buildingId, bankAccountId } });
    return response.data;
  },
  get: async (id: string, buildingId: string) => {
    const response = await api.get(`/bank-transactions/${id}`, { params: { buildingId } });
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/bank-transactions', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/bank-transactions/${id}`, data);
    return response.data;
  },
  delete: async (id: string, buildingId: string) => {
    const response = await api.delete(`/bank-transactions/${id}`, { params: { buildingId } });
    return response.data;
  },
  import: async (buildingId: string, transactions: any[]) => {
    const response = await api.post('/bank-transactions/import', { buildingId, transactions });
    return response.data;
  },
  // Explain a bank line with no sub-ledger item as a manual journal
  // (reconciliation Req 14): posts Dr/Cr the chosen nominal against 1900 Bank
  // and marks the line reconciled.
  explainJournal: async (
    id: string,
    buildingId: string,
    opts: { nominalCode: string; categoryName?: string; description?: string },
  ) => {
    const response = await api.put(`/bank-transactions/${id}`, {
      action: 'explain-journal',
      buildingId,
      ...opts,
    });
    return response.data;
  },
  // Attachments (reconciliation Req 13): pre-signed S3 upload, record the key,
  // and pre-signed download.
  getAttachmentUploadUrl: async (
    id: string,
    buildingId: string,
    filename: string,
    contentType?: string,
  ): Promise<{ uploadUrl: string; key: string }> => {
    const response = await api.put(`/bank-transactions/${id}`, {
      action: 'attachment-upload-url',
      buildingId,
      filename,
      contentType,
    });
    return response.data;
  },
  addAttachment: async (id: string, buildingId: string, key: string) => {
    const response = await api.put(`/bank-transactions/${id}`, {
      action: 'add-attachment',
      buildingId,
      key,
    });
    return response.data;
  },
  getAttachmentDownloadUrl: async (
    id: string,
    buildingId: string,
    key: string,
  ): Promise<{ downloadUrl: string }> => {
    const response = await api.get(`/bank-transactions/${id}`, {
      params: { buildingId, action: 'attachment-download-url', key },
    });
    return response.data;
  },
};

export const categoriesApi = {
  getAll: async (params?: { search?: string; type?: string }) => {
    const response = await api.get('/categories', { params });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/categories/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/categories', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export const freeagentImportApi = {
  validate: async (data: { buildingId?: string; data: Record<string, string> }) => {
    const response = await api.post('/freeagent-import', { ...data, mode: 'validate' }, {
      validateStatus: (status) => status === 200,
    });
    return response.data;
  },
  resolve: async (data: {
    buildingId?: string;
    data: Record<string, string>;
    resolutions: Array<{
      code: string;
      name: string;
      categoryType: 'income' | 'expense' | 'asset' | 'liability' | 'equity';
      statementSection: 'profit-loss' | 'balance-sheet';
      parentCode?: string;
    }>;
  }) => {
    const response = await api.post('/freeagent-import', { ...data, mode: 'resolve' }, {
      validateStatus: (status) => status === 200,
    });
    return response.data;
  },
  // Preview: run parse + classification against the selected building and
  // return a per-contact classification plus each contact's invoice count,
  // so the treasurer can override (assign-to-unit) or ignore contacts
  // before Confirm. No persistence happens on preview.
  preview: async (data: { buildingId?: string; data: Record<string, string> }) => {
    const response = await api.post('/freeagent-import', { ...data, mode: 'preview' }, {
      validateStatus: (status) => status === 200,
    });
    return response.data;
  },
  import: async (data: {
    buildingId?: string;
    data: Record<string, string>;
    overrides?: {
      contacts?: Record<
        string,
        {
          action: 'accept' | 'assign-unit' | 'ignore';
          unitId?: string;
        }
      >;
    };
  }) => {
    // Accept 207 (partial success with step errors), 201 (created), 409
    // (validation gate — unresolved codes), and 400 (invalid overrides
    // per the ignore-with-invoices rule) so the UI can render each
    // outcome distinctly instead of throwing.
    const response = await api.post('/freeagent-import', data, {
      validateStatus: (status) =>
        status === 201 || status === 207 || status === 409 || status === 400,
    });
    return response.data;
  },
  // Analyse (Bucket D3): first-import setup analyser. Given the same
  // payload the treasurer just previewed, derive a `SetupPlan` naming the
  // gaps (management start date, service-charge plan, per-unit shares,
  // retro-reclassify candidates) that need confirming before Confirm
  // Import. Returns the raw SetupPlan JSON from the Lambda so the wizard
  // can render each gap.
  analyse: async (data: {
    buildingId: string;
    data: Record<string, string>;
    overrides?: {
      contacts?: Record<
        string,
        {
          action: 'accept' | 'assign-unit' | 'ignore';
          unitId?: string;
        }
      >;
    };
  }) => {
    const response = await api.post(
      '/freeagent-import',
      { ...data, mode: 'analyse' },
      {
        validateStatus: (status) =>
          status === 200 || status === 400 || status === 404,
      },
    );
    return response.data;
  },
  // Apply setup (Bucket D4): atomically apply the treasurer-approved gaps
  // (management start date + service-charge plan + per-unit shares) from
  // an analyse response. Retro-reclassify writes are deferred to D5 and
  // silently skipped; the response summarises what was applied. Uses a
  // single DynamoDB TransactWriteCommand server-side.
  applySetup: async (data: {
    buildingId: string;
    accepted: {
      gaps: Array<Record<string, unknown>>;
    };
  }) => {
    const response = await api.post(
      '/freeagent-import',
      { ...data, mode: 'apply-setup' },
      {
        validateStatus: (status) =>
          status === 200 || status === 400 || status === 500,
      },
    );
    return response.data;
  },
};

// API service for management fee allocations
export const managementFeesApi = {
  getAll: async (buildingId?: string) => {
    const response = await api.get('/management-fee-allocations', {
      params: buildingId ? { buildingId } : {},
    });
    return response.data;
  },
  get: async (id: string) => {
    const response = await api.get(`/management-fee-allocations/${id}`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/management-fee-allocations', data);
    return response.data;
  },
  update: async (id: string, data: any) => {
    const response = await api.put(`/management-fee-allocations/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/management-fee-allocations/${id}`);
    return response.data;
  },
  generate: async (params: { period?: string; dryRun?: boolean }) => {
    const response = await api.post('/management-fee-allocations/generate', params);
    return response.data;
  },
};

export default api;
